import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import DemoViewBanner from '../demo/DemoViewBanner';
import SchoolAvatar from './SchoolAvatar';
import { getActiveSchool, getActiveSchoolId, setActiveSchool } from '../utils/activeSchool';
import PendingActionsPanel from './PendingActionsPanel';
import ToastStack from './ToastStack';
import useUndoActions from '../hooks/useUndoActions';
import { getApiBaseUrl } from '../apiBase';
import SportTypeSelector from './SportTypeSelector';
import AddressInput from './address/AddressInput';
import LocationMap from './address/LocationMap';
import { schoolGeoFromApi } from './address/addressGeo';
import { isFreelanceCoach, canFindSchoolAsCoach } from '../utils/roles';

// Главная страница школы — показывает сводку по всем показателям
export default function SchoolDashboard() {
    const { t, i18n } = useTranslation(['school', 'dashboard', 'common', 'errors', 'messages']);
    const [school, setSchool] = useState(null);
    const [stats, setStats] = useState(null);
    const [plans, setPlans] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [ownedSchools, setOwnedSchools] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creating, setCreating] = useState(false);
    const [planSaving, setPlanSaving] = useState(false);
    const [eventSaving, setEventSaving] = useState(false);
    const [planFilter, setPlanFilter] = useState('all');
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [planEditForm, setPlanEditForm] = useState({
        name: '',
        type: 'fixed',
        lessonsCount: '',
        validDays: '',
        description: '',
        isAttendanceFallback: false
    });
    const [priceForms, setPriceForms] = useState({});
    const { pendingActions, toasts, scheduleUndoableAction, cancelPendingAction, executePendingActionNow, dismissToast } = useUndoActions(10000);
    const token = localStorage.getItem('token');
    const accountRole = localStorage.getItem('role') || '';
    const canOwnSchools = accountRole === 'Owner' || accountRole === 'Admin';
    const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    });
    const inputStyle = {
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
        outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
        fontFamily: 'inherit'
    };
    const [planForm, setPlanForm] = useState({
        name: '',
        type: 'fixed',
        lessonsCount: '',
        validDays: '',
        price: '',
        description: '',
        isAttendanceFallback: false
    });
    const [eventForm, setEventForm] = useState({
        title: '',
        city: '',
        location: '',
        startDate: '',
        endDate: '',
        ticketCost: '',
        transferCost: '',
        hotelCost: '',
        otherCost: '',
        transferDetails: '',
        roomName: ''
    });
    useEffect(() => {
        let cancelled = false;
        loadDashboard(() => cancelled);
        return () => { cancelled = true; };
    }, [i18n.language]);

    useEffect(() => {
        if (loading) return;
        if (window.location.hash !== '#tariffs') return;
        const el = document.getElementById('tariffs');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [loading, plans.length]);

    async function loadDashboard(isCancelled = () => false) {
        setLoading(true);
        setShowCreateForm(false);
        setError('');
        try {
            if (!canOwnSchools) {
                let active = getActiveSchool();
                if (!active?.schoolId) {
                    const availRes = await api.get('/school/available');
                    const avail = availRes.data || [];
                    if (isCancelled()) return;
                    if (avail.length === 0) {
                        setError('no-access');
                        setLoading(false);
                        return;
                    }
                    if (avail.length === 1) {
                        const s = avail[0];
                        setActiveSchool({
                            schoolId: s.schoolId,
                            schoolName: s.schoolName,
                            schoolLogo: s.schoolLogo,
                            accessRole: s.accessRole,
                            studentId: s.studentId ?? null
                        });
                        active = getActiveSchool();
                    } else {
                        setError('pick-school');
                        setLoading(false);
                        return;
                    }
                }

                const sid = active.schoolId;
                const schoolRes = await api.get(`/school/${sid}/brief`);
                if (isCancelled()) return;
                setSchool(schoolRes.data);
                setOwnedSchools([]);
                const groupsRes = await api.get(`/school/${sid}/groups`);
                const groups = groupsRes.data || [];
                setPlans([]);
                setEvents([]);
                setStats({
                    groupCount: groups.length,
                    studentCount: groups.reduce((sum, g) => sum + (g.studentCount || 0), 0),
                    groups
                });
                setLoading(false);
                return;
            }

            const listRes = await api.get('/school/my/list');
            const owned = listRes.data || [];
            setOwnedSchools(owned);

            const active = getActiveSchool();
            const viewingDemo = active?.isDemoSchool || active?.accessRole === 'demo';

            if (viewingDemo && active?.schoolId) {
                const schoolId = active.schoolId;
                const schoolRes = await api.get('/school/my', { params: { schoolId } });
                if (isCancelled()) return;
                setSchool(schoolRes.data);

                const groupsRes = await api.get(`/school/${schoolId}/groups`);
                const plansRes = await api.get(`/subscriptionplan?schoolId=${schoolId}`);
                const eventsRes = await api.get(`/event?schoolId=${schoolId}`);
                setPlans(plansRes.data || []);
                setEvents(eventsRes.data || []);
                const groups = groupsRes.data || [];
                const total = groups.reduce((sum, g) => sum + (g.studentCount || 0), 0);
                setStats({
                    groupCount: groups.length,
                    studentCount: total,
                    groups
                });
                setLoading(false);
                return;
            }

            if (owned.length === 0) {
                setError('no-school');
                setLoading(false);
                return;
            }

            const activeId = getActiveSchoolId();
            const schoolId = activeId && owned.some(s => s.id === activeId)
                ? activeId
                : owned[0].id;

            const picked = owned.find(s => s.id === schoolId);
            if (picked && !viewingDemo) {
                setActiveSchool({
                    schoolId: picked.id,
                    schoolName: picked.name,
                    schoolLogo: picked.logo,
                    accessRole: 'owner',
                    studentId: null
                });
            }

            const schoolRes = await api.get('/school/my', { params: { schoolId } });
            setSchool(schoolRes.data);

            const groupsRes = await api.get(`/school/${schoolId}/groups`);
            const plansRes = await api.get(`/subscriptionplan?schoolId=${schoolId}`);
            const eventsRes = await api.get(`/event?schoolId=${schoolId}`);
            setPlans(plansRes.data || []);
            setEvents(eventsRes.data || []);

            // Считаем общую статистику
            const groups = groupsRes.data;
            const total = groups.reduce((sum, g) => sum + (g.studentCount || 0), 0);
            setStats({
                groupCount: groups.length,
                studentCount: total,
                groups: groups
            });

        } catch (err) {
            if (isCancelled()) return;
            if (err.response?.status === 404) {
                setError('no-school'); // Школа ещё не создана
            } else if (err.response?.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            } else {
                const serverMessage = err.response?.data?.message;
                setError(serverMessage || t('school:alerts.loadFailed', { status: err.response?.status || 'network' }));
            }
        }
        if (!isCancelled()) setLoading(false);
    }

    // ── Форма создания школы ──────────────────────
    const [form, setForm] = useState({
        name: '', sportTypeId: '', country: '', city: '', address: '', phone: '', inn: '',
        lat: null, lng: null, region: '', postalCode: ''
    });
    const [pickedSportName, setPickedSportName] = useState('');
    const [pickedSportEmoji, setPickedSportEmoji] = useState('');

    useEffect(() => {
        if (showCreateForm && !form.country) {
            setForm((f) => ({ ...f, country: t('school:schoolForm.defaultCountry') }));
        }
    }, [showCreateForm, t, form.country]);

    async function handleCreate(e) {
        e.preventDefault();
        setCreating(true);
        try {
            const fromStorage = parseInt(localStorage.getItem('sportTypeId'), 10);
            const fromForm = parseInt(form.sportTypeId, 10);
            const userSportTypeId = fromStorage || fromForm;

            if (!userSportTypeId) {
                alert(t('school:alerts.pickSport'));
                setCreating(false);
                return;
            }

            const country = String(form.country || '').trim();
            const city = String(form.city || '').trim();
            if (country.length < 2) {
                alert(t('school:alerts.countryRequired'));
                setCreating(false);
                return;
            }
            if (city.length < 2) {
                alert(t('school:alerts.cityRequired'));
                setCreating(false);
                return;
            }

            const inn = String(form.inn || '').trim().replace(/\s/g, '');
            if (!inn) {
                alert(t('school:alerts.innRequired'));
                setCreating(false);
                return;
            }
            if (!/^\d{10}$/.test(inn) && !/^\d{12}$/.test(inn)) {
                alert(t('school:alerts.innFormat'));
                setCreating(false);
                return;
            }

            const res = await api.post('/school', {
                name: form.name,
                sportTypeId: userSportTypeId,
                country,
                city: form.city || city,
                address: form.address || '',
                addressFull: form.address || '',
                region: form.region || null,
                postalCode: form.postalCode || null,
                lat: form.lat,
                lng: form.lng,
                phone: form.phone || '',
                inn
            });
            if (!fromStorage && fromForm) {
                localStorage.setItem('sportTypeId', String(userSportTypeId));
                if (pickedSportName) {
                    localStorage.setItem('sportType', pickedSportName);
                    localStorage.setItem('sportIcon', pickedSportEmoji || '');
                }
            }
            const newId = res.data.schoolId;
            setActiveSchool({
                schoolId: newId,
                schoolName: form.name,
                schoolLogo: null,
                accessRole: 'owner',
                studentId: null
            });
            setError('');
            setShowCreateForm(false);
            loadDashboard();
        } catch (err) {
            const data = err.response?.data;
            if (data?.error === 'schools_limit_reached') {
                alert(`${data.message}\n\nСейчас: ${data.schoolsCount ?? '?'} из ${data.maxSchools ?? '?'} школ (тариф «${data.currentPlan}»).\nОткройте /billing/plans для смены тарифа.`);
            } else {
                alert(data?.message || t('school:alerts.createFailed'));
            }
        }
        setCreating(false);
    }

    async function handleCreatePlan(e) {
        e.preventDefault();
        setPlanSaving(true);
        try {
            const sid = school?.id || getActiveSchoolId();
            await api.post('/subscriptionplan', {
                name: planForm.name,
                description: planForm.description || '',
                type: planForm.type,
                lessonsCount: planForm.type === 'unlimited' ? null : (planForm.lessonsCount ? parseInt(planForm.lessonsCount, 10) : null),
                validDays: planForm.validDays ? parseInt(planForm.validDays, 10) : null,
                price: parseFloat(planForm.price),
                isAttendanceFallback: planForm.type === 'per_visit' && planForm.isAttendanceFallback
            }, { params: { schoolId: sid } });
            setPlanForm({
                name: '',
                type: 'fixed',
                lessonsCount: '',
                validDays: '',
                price: '',
                description: '',
                isAttendanceFallback: false
            });
            loadDashboard();
        } catch (err) {
            alert(err.response?.data?.message || t('school:alerts.planCreateFailed'));
        }
        setPlanSaving(false);
    }

    async function handleCreateEvent(e) {
        e.preventDefault();
        setEventSaving(true);
        try {
            const sid = school?.id || getActiveSchoolId();
            await api.post('/event', {
                title: eventForm.title,
                description: '',
                startDate: eventForm.startDate || new Date().toISOString(),
                endDate: eventForm.endDate || null,
                city: eventForm.city || '',
                location: eventForm.location || '',
                roomName: eventForm.roomName || null,
                transferDetails: eventForm.transferDetails || null,
                ticketCost: eventForm.ticketCost ? parseFloat(eventForm.ticketCost) : 0,
                transferCost: eventForm.transferCost ? parseFloat(eventForm.transferCost) : 0,
                hotelCost: eventForm.hotelCost ? parseFloat(eventForm.hotelCost) : 0,
                otherCost: eventForm.otherCost ? parseFloat(eventForm.otherCost) : 0,
                receiptsJson: null,
                groupId: null,
                groupIds: []
            }, { params: { schoolId: sid } });
            setEventForm({
                title: '', city: '', location: '', startDate: '', endDate: '',
                ticketCost: '', transferCost: '', hotelCost: '', otherCost: '',
                transferDetails: '', roomName: ''
            });
            loadDashboard();
        } catch (err) {
            alert(err.response?.data?.message || t('school:alerts.eventCreateFailed'));
        }
        setEventSaving(false);
    }

    function startEditPlan(plan) {
        setEditingPlanId(plan.id);
        setPlanEditForm({
            name: plan.name || '',
            type: plan.type || 'fixed',
            lessonsCount: plan.lessonsCount ?? '',
            validDays: plan.validDays ?? '',
            description: plan.description || '',
            isAttendanceFallback: !!plan.isAttendanceFallback
        });
    }

    async function handleUpdatePlan(planId) {
        try {
            await api.put(`/subscriptionplan/${planId}`, {
                name: planEditForm.name,
                description: planEditForm.description || '',
                type: planEditForm.type,
                lessonsCount: planEditForm.type === 'unlimited' ? null : (planEditForm.lessonsCount === '' ? null : parseInt(planEditForm.lessonsCount, 10)),
                validDays: planEditForm.validDays === '' ? null : parseInt(planEditForm.validDays, 10),
                isAttendanceFallback: planEditForm.type === 'per_visit' && planEditForm.isAttendanceFallback
            });
            setEditingPlanId(null);
            loadDashboard();
        } catch (err) {
            alert(err.response?.data?.message || t('school:alerts.planUpdateFailed'));
        }
    }

    async function handleChangePrice(planId) {
        const raw = priceForms[planId];
        if (!raw) return alert(t('school:alerts.priceRequired'));
        try {
            await api.post(`/subscriptionplan/${planId}/price`, {
                price: parseFloat(raw)
            });
            setPriceForms(prev => ({ ...prev, [planId]: '' }));
            loadDashboard();
        } catch (err) {
            alert(err.response?.data?.message || t('school:alerts.priceUpdateFailed'));
        }
    }

    async function handleDeactivatePlan(planId) {
        const plan = plans.find(p => p.id === planId);
        scheduleUndoableAction(t('school:plans.deactivateUndo', { name: plan?.name || planId }), async () => {
            try {
                await api.post(`/subscriptionplan/${planId}/deactivate`);
                loadDashboard();
            } catch (err) {
                alert(err.response?.data?.message || t('school:alerts.planDeactivateFailed'));
            }
        });
    }

    const visiblePlans = plans.filter(p => {
        if (planFilter === 'active') return p.isActive;
        if (planFilter === 'inactive') return !p.isActive;
        return true;
    });

    // ── Рендер ───────────────────────────────────
    if (loading) return <div className="loading">{t('common:loading')}</div>;

    if (error === 'no-access') {
        return (
            <OwnerPageLayout maxWidth={480}>
                <div style={{ margin: '40px auto', padding: '24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px' }}>🏫</div>
                    <h2 style={{ color: '#1A1A1A' }}>{t('school:noAccess.title')}</h2>
                    <p style={{ color: '#666' }}>
                        {t('school:noAccess.hint', { email: localStorage.getItem('username') || t('common:yourEmail', { defaultValue: 'ваш email' }) })}
                    </p>
                </div>
            </OwnerPageLayout>
        );
    }

    if (error === 'pick-school') {
        window.location.href = '/select-school';
        return null;
    }

    if ((error === 'no-school' || showCreateForm) && !canOwnSchools) {
        return (
            <OwnerPageLayout maxWidth={480}>
                <div style={{ margin: '40px auto', padding: '24px', textAlign: 'center' }}>
                    <p style={{ color: '#666' }}>{t('school:coachNoCreate')}</p>
                    <a href="/select-school" style={{ color: '#EB3333', fontWeight: 600 }}>{t('school:selectSchool')}</a>
                </div>
            </OwnerPageLayout>
        );
    }

    if ((error === 'no-school' || showCreateForm) && canOwnSchools) {
        const userSportTypeId = localStorage.getItem('sportTypeId');
        const userSportType = localStorage.getItem('sportType');
        const userSportIcon = localStorage.getItem('sportIcon');

        return (
            <OwnerPageLayout maxWidth={480}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ width: '100%', maxWidth: '480px' }}>

                    {/* Заголовок */}
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{ fontSize: '52px', marginBottom: '12px' }}>🏫</div>
                        <h1 style={{ fontSize: '26px', fontWeight: '800', margin: '0 0 8px', color: '#1A1A1A' }}>
                            {error === 'no-school' ? t('school:create.titleNew') : t('school:create.titleAnother')}
                        </h1>
                        <p style={{ color: '#888', fontSize: '15px', margin: 0 }}>
                            {error === 'no-school'
                                ? t('school:create.subtitleNew')
                                : t('school:create.subtitleAnother')}
                        </p>
                        {showCreateForm && ownedSchools.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowCreateForm(false)}
                                style={{
                                    marginTop: '12px', padding: '8px 16px',
                                    border: '1px solid #ddd', borderRadius: '8px',
                                    background: 'white', cursor: 'pointer'
                                }}
                            >
                                {t('school:create.backToSchool')}
                            </button>
                        )}
                    </div>

                    <div style={{
                        background: 'white', borderRadius: '20px',
                        padding: '36px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid var(--sp-gray-100)'
                    }}>

                        {userSportTypeId ? (
                            <div style={{
                                background: '#f0ebff', borderRadius: '12px',
                                padding: '16px 20px', marginBottom: '24px',
                                display: 'flex', alignItems: 'center', gap: '14px'
                            }}>
                                <span style={{ fontSize: '36px' }}>{userSportIcon || '🏅'}</span>
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '16px', color: '#EB3333' }}>
                                        {userSportType || t('school:create.sportType')}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                                        {t('school:create.sportFromAccount')}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block', fontSize: '13px',
                                    fontWeight: '600', color: '#444', marginBottom: '8px'
                                }}>
                                    {t('school:create.sportTypeRequired')}
                                </label>
                                {pickedSportName && (
                                    <p style={{ fontSize: 13, color: '#EB3333', margin: '0 0 8px', fontWeight: 600 }}>
                                        {t('school:create.selected', { emoji: pickedSportEmoji, name: pickedSportName })}
                                    </p>
                                )}
                                <SportTypeSelector
                                    value={form.sportTypeId}
                                    compact
                                    onChange={(id, sport) => {
                                        setForm({ ...form, sportTypeId: String(id) });
                                        setPickedSportName(sport?.name || '');
                                        setPickedSportEmoji(sport?.emoji || '');
                                    }}
                                />
                            </div>
                        )}

                        <form onSubmit={handleCreate}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block', fontSize: '13px',
                                    fontWeight: '600', color: '#444', marginBottom: '6px'
                                }}>
                                    {t('school:create.schoolNameLabel')}
                                </label>
                                <input
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder={t('school:create.namePlaceholder')}
                                    required
                                    style={inputStyle}
                                    onFocus={e => e.target.style.borderColor = '#EB3333'}
                                    onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{
                                        display: 'block', fontSize: '13px',
                                        fontWeight: '600', color: '#444', marginBottom: '6px'
                                    }}>
                                        {t('school:create.countryLabel')}
                                    </label>
                                    <input
                                        value={form.country}
                                        onChange={e => setForm({ ...form, country: e.target.value })}
                                        placeholder={t('school:create.countryPlaceholder')}
                                        required
                                        minLength={2}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block', fontSize: '13px',
                                        fontWeight: '600', color: '#444', marginBottom: '6px'
                                    }}>
                                        {t('school:create.cityLabel')}
                                    </label>
                                    <input
                                        value={form.city}
                                        onChange={e => setForm({ ...form, city: e.target.value })}
                                        placeholder={t('school:create.cityPlaceholder')}
                                        required
                                        minLength={2}
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block', fontSize: '13px',
                                    fontWeight: '600', color: '#444', marginBottom: '6px'
                                }}>
                                    {t('school:create.addressLabel')}
                                </label>
                                <AddressInput
                                    address={form.address}
                                    lat={form.lat}
                                    lng={form.lng}
                                    city={form.city}
                                    country={form.country}
                                    inputStyle={inputStyle}
                                    placeholder={t('school:create.addressPlaceholder')}
                                    onChange={(v) => setForm({
                                        ...form,
                                        address: v.address || '',
                                        lat: v.lat ?? null,
                                        lng: v.lng ?? null,
                                        city: v.city || form.city,
                                        region: v.region || form.region,
                                        postalCode: v.postalCode || form.postalCode
                                    })}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block', fontSize: '13px',
                                    fontWeight: '600', color: '#444', marginBottom: '6px'
                                }}>
                                    {t('school:create.phoneLabel')}
                                </label>
                                <input
                                    value={form.phone}
                                    onChange={e => setForm({ ...form, phone: e.target.value })}
                                    placeholder="+7 (999) 123-45-67"
                                    style={inputStyle}
                                    onFocus={e => e.target.style.borderColor = '#EB3333'}
                                    onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                />
                            </div>

                            <div style={{ marginBottom: '28px' }}>
                                <label style={{
                                    display: 'block', fontSize: '13px',
                                    fontWeight: '600', color: '#444', marginBottom: '6px'
                                }}>
                                    {t('school:create.innLabel')}
                                </label>
                                <input
                                    value={form.inn}
                                    onChange={e => setForm({ ...form, inn: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                                    placeholder="7701234567"
                                    required
                                    inputMode="numeric"
                                    style={inputStyle}
                                    onFocus={e => e.target.style.borderColor = '#EB3333'}
                                    onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                    {t('school:create.innHint')}
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={creating}
                                className="btn-create-school form-submit"
                                style={{
                                    width: '100%', padding: '14px',
                                    background: creating ? '#94a3b8' : '#1A1A1A',
                                    color: 'white', border: 'none', borderRadius: '10px',
                                    fontSize: '15px', fontWeight: '700',
                                    cursor: creating ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {creating ? t('school:create.submitting') : t('school:create.submit')}
                            </button>
                        </form>
                    </div>
                </div>
                </div>
            </OwnerPageLayout>
        );
    }

    if (error) return <div className="empty">{error}</div>;

    // Основной дашборд
    return (
        <OwnerPageLayout
            maxWidth={960}
            beforeMain={
                <>
                    <ToastStack toasts={toasts} onDismiss={dismissToast} />
                    <DemoViewBanner />
                    {canFindSchoolAsCoach() && (
                        <div style={{
                            maxWidth: 960, margin: '16px auto 0', padding: '0 24px'
                        }}>
                            <div style={{
                                background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 12,
                                padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
                            }}>
                                <span style={{ fontSize: 14, color: '#3730a3' }}>
                                    {t('school:coachBanner.text')}
                                </span>
                                <a href="/coaches/find-school" style={{
                                    padding: '8px 14px', borderRadius: 8, background: '#EB3333', color: '#fff',
                                    textDecoration: 'none', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap'
                                }}>
                                    {t('school:coachBanner.link')}
                                </a>
                            </div>
                        </div>
                    )}
                </>
            }
        >
            <div data-onboarding="dashboard-main">
            <PendingActionsPanel
                pendingActions={pendingActions}
                onCancel={cancelPendingAction}
                onExecuteNow={executePendingActionNow}
            />

            {/* Шапка школы */}
            <div style={{
                background: '#1A1A1A', color: 'white',
                borderRadius: '12px', padding: '24px',
                marginBottom: '24px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <div>
                    <h1 data-onboarding="school-name" style={{ fontSize: '24px', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <SchoolAvatar logoPath={school.logo} size={48} alt={school.name} />
                        {school.name}
                    </h1>
                    <p style={{ color: '#aaa', margin: '6px 0 0', fontSize: '14px' }}>
                        {school.sportType} · {school.addressFull || school.address}
                        {school.inn && <span>{t('school:header.inn', { inn: school.inn })}</span>}
                    </p>
                    {(() => {
                        const geo = schoolGeoFromApi(school);
                        if (geo.lat == null || geo.lng == null) return null;
                        return (
                            <div style={{ marginTop: 12, maxWidth: 320 }}>
                                <LocationMap
                                    lat={geo.lat}
                                    lng={geo.lng}
                                    draggable={false}
                                    height="120px"
                                    zoom={15}
                                    alwaysShow
                                />
                                <a
                                    href="/school/settings"
                                    style={{ fontSize: 12, color: '#c4b5fd', marginTop: 6, display: 'inline-block' }}
                                >
                                    {t('school:header.changeAddressMap')}
                                </a>
                            </div>
                        );
                    })()}
                    {canOwnSchools && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                            <a
                                href="/school/settings"
                                style={{
                                    display: 'inline-block',
                                    padding: '8px 14px', borderRadius: '8px',
                                    background: 'rgba(108,63,232,0.35)', color: '#e9d5ff',
                                    textDecoration: 'none', fontSize: '13px', fontWeight: '600'
                                }}
                            >
                                {t('school:header.settings')}
                            </a>
                            {canFindSchoolAsCoach() && (
                                <a
                                    href="/coaches/find-school"
                                    style={{
                                        display: 'inline-block',
                                        padding: '8px 14px', borderRadius: '8px',
                                        background: 'rgba(59,130,246,0.35)', color: '#bfdbfe',
                                        textDecoration: 'none', fontSize: '13px', fontWeight: '600'
                                    }}
                                >
                                    {t('school:header.findSchool')}
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowCreateForm(true)}
                                style={{
                                    padding: '8px 14px', borderRadius: '8px',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    background: 'transparent', color: '#e9d5ff',
                                    cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                                }}
                            >
                                {t('school:header.anotherSchool')}
                            </button>
                        </div>
                    )}
                </div>
                <div style={{ textAlign: 'right', fontSize: '14px', color: '#aaa' }}>
                    <div>{school.phone}</div>
                </div>
            </div>

            {canOwnSchools && ownedSchools.length > 1 && (
                <div style={{
                    background: 'white', borderRadius: '12px', padding: '16px 20px',
                    marginBottom: '20px', border: '1px solid var(--sp-gray-100)'
                }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '12px' }}>
                        {t('school:header.mySchools')}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {ownedSchools.map(s => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => {
                                    setActiveSchool({
                                        schoolId: s.id,
                                        schoolName: s.name,
                                        schoolLogo: s.logo,
                                        accessRole: 'owner',
                                        studentId: null
                                    });
                                    loadDashboard();
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '8px 14px', borderRadius: '999px',
                                    border: s.id === school?.id ? '2px solid #EB3333' : '1px solid #ddd',
                                    background: s.id === school?.id ? '#f3eeff' : 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                <SchoolAvatar logoPath={s.logo} size={28} alt={s.name} />
                                <span style={{ fontWeight: '600', fontSize: '13px' }}>{s.name}</span>
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setShowCreateForm(true)}
                            style={{
                                padding: '8px 14px', borderRadius: '999px',
                                border: '1px dashed #EB3333', background: 'white',
                                color: '#EB3333', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                            }}
                        >
                            {t('school:header.addSchool')}
                        </button>
                    </div>
                </div>
            )}

            <div data-onboarding="dashboard-metrics" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '16px', marginBottom: '24px'
            }}>
                <StatCard icon="👥" label={t('school:stats.students')} value={stats?.studentCount || 0} color="#3498db" />
                <StatCard icon="🏷️" label={t('school:stats.groups')} value={stats?.groupCount || 0} color="#2ecc71" />
            </div>

            <div data-onboarding="next-lesson-card" style={{
                background: 'white', borderRadius: '12px', padding: '16px 20px',
                marginBottom: '24px', border: '1px solid var(--sp-gray-100)'
            }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#EB3333', marginBottom: 6 }}>{t('school:stats.nextLesson')}</div>
                <div style={{ fontSize: '13px', color: '#555' }}>
                    {stats?.groups?.[0]?.name ? t('school:stats.groupSchedule', { name: stats.groups[0].name }) : t('school:stats.openSchedule')}
                </div>
            </div>

            {/* Список групп */}
            <div style={{
                background: 'white', borderRadius: '12px',
                padding: '20px', marginBottom: '24px'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '16px'
                }}>
                    <h3 style={{ margin: 0 }}>🏷️ {canOwnSchools ? t('school:groups.titleOwner') : t('school:groups.titleCoach')}</h3>
                    {canOwnSchools ? (
                        <a href="/students" style={{
                            background: '#1A1A1A', color: 'white',
                            padding: '8px 16px', borderRadius: '8px', fontSize: '14px', textDecoration: 'none'
                        }}>
                            {t('school:groups.addStudent')}
                        </a>
                    ) : (
                        <a href="/attendance" style={{
                            background: '#1A1A1A', color: 'white',
                            padding: '8px 16px', borderRadius: '8px', fontSize: '14px', textDecoration: 'none'
                        }}>
                            {t('school:groups.markAttendance')}
                        </a>
                    )}
                </div>

                {stats?.groups?.length === 0 ? (
                    <p style={{ color: '#888', fontSize: '14px' }}>
                        {canOwnSchools
                            ? t('school:groups.emptyOwner')
                            : t('school:groups.emptyCoach')}
                    </p>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {stats?.groups?.map(g => (
                            <div key={g.id} style={{
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', padding: '12px 16px',
                                border: '1px solid #eee', borderRadius: '8px'
                            }}>
                                <div>
                                    <div style={{ fontWeight: 'bold' }}>{g.name}</div>
                                    <div style={{ fontSize: '13px', color: '#888' }}>
                                        {t('school:groups.coach', { name: g.coachName })}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        background: '#e8f5e9', color: '#2e7d32',
                                        padding: '4px 12px', borderRadius: '20px', fontSize: '13px'
                                    }}>
                                        {t('school:groups.studentsShort', { count: g.studentCount })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {canOwnSchools && (
            <div
                id="tariffs"
                style={{
                background: 'white', borderRadius: '12px',
                padding: '20px', marginBottom: '24px',
                scrollMarginTop: '80px'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '16px'
                }}>
                    <h3 style={{ margin: 0 }}>🎫 {t('school:plans.title')}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <select
                            value={planFilter}
                            onChange={e => setPlanFilter(e.target.value)}
                            style={{
                                border: '1px solid var(--sp-gray-200)',
                                borderRadius: '8px',
                                padding: '6px 10px',
                                background: 'white',
                                fontSize: '13px'
                            }}
                        >
                            <option value="all">{t('school:plans.filterAll')}</option>
                            <option value="active">{t('school:plans.filterActive')}</option>
                            <option value="inactive">{t('school:plans.filterInactive')}</option>
                        </select>
                        <span style={{ fontSize: '13px', color: '#888' }}>
                            {t('school:plans.ofTotal', { visible: visiblePlans.length, total: plans.length })}
                        </span>
                    </div>
                </div>

                <form onSubmit={handleCreatePlan} style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <input
                            required
                            placeholder={t('school:plans.namePlaceholder')}
                            value={planForm.name}
                            onChange={e => setPlanForm({ ...planForm, name: e.target.value })}
                            style={inputStyle}
                        />
                        <select
                            value={planForm.type}
                            onChange={e => {
                                const type = e.target.value;
                                setPlanForm({
                                    ...planForm,
                                    type,
                                    isAttendanceFallback: type === 'per_visit' ? planForm.isAttendanceFallback : false
                                });
                            }}
                            style={{ ...inputStyle, background: 'white' }}
                        >
                            <option value="fixed">{t('school:plans.typeFixed')}</option>
                            <option value="per_visit">{t('school:plans.typePerVisit')}</option>
                            <option value="unlimited">{t('school:plans.typeUnlimited')}</option>
                        </select>
                        <input
                            type="number"
                            min="0"
                            placeholder={t('school:plans.lessonsPlaceholder')}
                            value={planForm.lessonsCount}
                            disabled={planForm.type === 'unlimited'}
                            onChange={e => setPlanForm({ ...planForm, lessonsCount: e.target.value })}
                            style={inputStyle}
                        />
                        <input
                            type="number"
                            min="1"
                            step="0.01"
                            required
                            placeholder={t('school:plans.pricePlaceholder')}
                            value={planForm.price}
                            onChange={e => setPlanForm({ ...planForm, price: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr auto', gap: '10px' }}>
                        <input
                            type="number"
                            min="0"
                            placeholder={t('school:plans.daysPlaceholder')}
                            value={planForm.validDays}
                            onChange={e => setPlanForm({ ...planForm, validDays: e.target.value })}
                            style={inputStyle}
                        />
                        <input
                            placeholder={t('school:plans.descPlaceholder')}
                            value={planForm.description}
                            onChange={e => setPlanForm({ ...planForm, description: e.target.value })}
                            style={inputStyle}
                        />
                        <button
                            type="submit"
                            disabled={planSaving}
                            style={{
                                background: planSaving ? '#94a3b8' : '#1A1A1A',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '0 18px',
                                cursor: planSaving ? 'not-allowed' : 'pointer',
                                fontWeight: '700'
                            }}
                        >
                            {planSaving ? t('school:plans.adding') : t('school:plans.add')}
                        </button>
                    </div>
                    <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        marginTop: '10px',
                        fontSize: '13px',
                        color: planForm.type === 'per_visit' ? '#444' : '#999',
                        cursor: planForm.type === 'per_visit' ? 'pointer' : 'not-allowed'
                    }}>
                        <input
                            type="checkbox"
                            disabled={planForm.type !== 'per_visit'}
                            checked={planForm.isAttendanceFallback}
                            onChange={e => setPlanForm({ ...planForm, isAttendanceFallback: e.target.checked })}
                            style={{ marginTop: '2px' }}
                        />
                        <span>
                            {t('school:plans.fallbackCharge')}
                            {' '}{t('school:plans.fallbackChargeHint')}
                            {plans.some(p => p.isAttendanceFallback) && !planForm.isAttendanceFallback && (
                                <span style={{ display: 'block', color: '#b45309', marginTop: '4px' }}>
                                    {t('school:plans.fallbackWarning')}
                                </span>
                            )}
                        </span>
                    </label>
                </form>

                {visiblePlans.length === 0 ? (
                    <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>
                        {plans.length === 0
                            ? t('school:plans.empty')
                            : t('school:plans.emptyFilter')}
                    </p>
                ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                        {visiblePlans.map(p => (
                            <div key={p.id} style={{
                                border: '1px solid #eee',
                                borderRadius: '10px',
                                padding: '12px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            {p.name}
                                            {p.isAttendanceFallback && (
                                                <span style={{ fontSize: '12px', color: '#1d4ed8', background: '#eff6ff', padding: '2px 8px', borderRadius: '12px' }}>
                                                    {t('school:plans.singleVisitBadge')}
                                                </span>
                                            )}
                                            {!p.isActive && (
                                                <span style={{ fontSize: '12px', color: '#b45309', background: '#fff7ed', padding: '2px 8px', borderRadius: '12px' }}>
                                                    {t('school:plans.inactiveBadge')}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#888' }}>
                                            {p.lessonsCount ? t('school:plans.lessonsCount', { count: p.lessonsCount }) : t('school:plans.unlimited')}
                                            {p.validDays ? t('school:plans.daysSuffix', { count: p.validDays }) : ''}
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: '800' }}>
                                        {p.currentPrice?.toLocaleString('ru-RU')} ₽
                                    </div>
                                </div>

                                {editingPlanId === p.id ? (
                                    <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                                            <input value={planEditForm.name} onChange={e => setPlanEditForm({ ...planEditForm, name: e.target.value })} style={inputStyle} />
                                            <select
                                                value={planEditForm.type}
                                                onChange={e => {
                                                    const type = e.target.value;
                                                    setPlanEditForm({
                                                        ...planEditForm,
                                                        type,
                                                        isAttendanceFallback: type === 'per_visit' ? planEditForm.isAttendanceFallback : false
                                                    });
                                                }}
                                                style={{ ...inputStyle, background: 'white' }}
                                            >
                                                <option value="fixed">{t('school:plans.typeFixed')}</option>
                                                <option value="per_visit">{t('school:plans.typePerVisit')}</option>
                                                <option value="unlimited">{t('school:plans.typeUnlimited')}</option>
                                            </select>
                                            <input
                                                type="number"
                                                min="0"
                                                value={planEditForm.lessonsCount}
                                                disabled={planEditForm.type === 'unlimited'}
                                                onChange={e => setPlanEditForm({ ...planEditForm, lessonsCount: e.target.value })}
                                                style={inputStyle}
                                                placeholder={t('school:plans.editLessons')}
                                            />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr auto auto', gap: '8px' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                value={planEditForm.validDays}
                                                onChange={e => setPlanEditForm({ ...planEditForm, validDays: e.target.value })}
                                                style={inputStyle}
                                                placeholder={t('school:plans.daysPlaceholder')}
                                            />
                                            <input
                                                value={planEditForm.description}
                                                onChange={e => setPlanEditForm({ ...planEditForm, description: e.target.value })}
                                                style={inputStyle}
                                                placeholder={t('school:plans.editDesc')}
                                            />
                                            <button onClick={() => handleUpdatePlan(p.id)} style={{ border: 'none', borderRadius: '8px', padding: '0 14px', background: '#1A1A1A', color: 'white', cursor: 'pointer' }}>
                                                {t('common:save')}
                                            </button>
                                            <button onClick={() => setEditingPlanId(null)} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '0 14px', background: 'white', cursor: 'pointer' }}>
                                                {t('common:cancel')}
                                            </button>
                                        </div>
                                        <label style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            fontSize: '13px',
                                            color: planEditForm.type === 'per_visit' ? '#444' : '#999',
                                            cursor: planEditForm.type === 'per_visit' ? 'pointer' : 'not-allowed'
                                        }}>
                                            <input
                                                type="checkbox"
                                                disabled={planEditForm.type !== 'per_visit'}
                                                checked={planEditForm.isAttendanceFallback}
                                                onChange={e => setPlanEditForm({ ...planEditForm, isAttendanceFallback: e.target.checked })}
                                                style={{ marginTop: '2px' }}
                                            />
                                            <span>
                                                {t('school:plans.fallbackCharge')}
                                                {' '}{t('school:plans.fallbackChargeShortHint')}
                                            </span>
                                        </label>
                                    </div>
                                ) : (
                                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button onClick={() => startEditPlan(p)} style={{ border: '1px solid #ddd', borderRadius: '8px', background: 'white', padding: '6px 10px', cursor: 'pointer' }}>
                                            {t('common:edit')}
                                        </button>
                                        {p.isActive && (
                                            <button onClick={() => handleDeactivatePlan(p.id)} style={{ border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '8px', background: '#fff5f5', padding: '6px 10px', cursor: 'pointer' }}>
                                                {t('school:plans.deactivate')}
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' }}>
                                    <input
                                        type="number"
                                        min="1"
                                        step="0.01"
                                        placeholder={t('school:plans.newPrice')}
                                        value={priceForms[p.id] || ''}
                                        onChange={e => setPriceForms(prev => ({ ...prev, [p.id]: e.target.value }))}
                                        style={inputStyle}
                                    />
                                    <button
                                        onClick={() => handleChangePrice(p.id)}
                                        style={{ border: 'none', borderRadius: '8px', padding: '0 12px', background: '#EB3333', color: 'white', cursor: 'pointer' }}
                                    >
                                        {t('school:plans.updatePrice')}
                                    </button>
                                </div>

                                <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                                    {t('school:plans.priceHistory')}
                                    {(p.priceHistory || []).length === 0 ? (
                                        <span>{t('school:plans.noPriceHistory')}</span>
                                    ) : (
                                        <div style={{ marginTop: '4px', display: 'grid', gap: '4px' }}>
                                            {(p.priceHistory || []).map(h => (
                                                <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>
                                                        {formatDate(h.validFrom)}
                                                        {h.validTo ? ` - ${formatDate(h.validTo)}` : t('school:plans.validToOpen')}
                                                    </span>
                                                    <span style={{ fontWeight: h.isCurrent ? '700' : '400' }}>
                                                        {h.price?.toLocaleString('ru-RU')} ₽ {h.isCurrent ? t('school:plans.currentPriceTag') : ''}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            )}

            {canOwnSchools && (
            <div style={{
                background: 'white', borderRadius: '12px',
                padding: '20px', marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0 }}>{t('school:eventsSection.title')}</h3>
                    <span style={{ fontSize: '13px', color: '#888' }}>{t('school:eventsSection.count', { count: events.length })}</span>
                </div>
                <form onSubmit={handleCreateEvent} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <input required placeholder={t('school:events.namePlaceholder')} value={eventForm.title} onChange={e => setEventForm({ ...eventForm, title: e.target.value })} style={inputStyle} />
                        <input placeholder={t('school:events.city')} value={eventForm.city} onChange={e => setEventForm({ ...eventForm, city: e.target.value })} style={inputStyle} />
                        <input placeholder={t('school:events.location')} value={eventForm.location} onChange={e => setEventForm({ ...eventForm, location: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <input type="date" value={eventForm.startDate} onChange={e => setEventForm({ ...eventForm, startDate: e.target.value })} style={inputStyle} />
                        <input type="date" value={eventForm.endDate} onChange={e => setEventForm({ ...eventForm, endDate: e.target.value })} style={inputStyle} />
                        <input placeholder={t('school:events.room')} value={eventForm.roomName} onChange={e => setEventForm({ ...eventForm, roomName: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '10px' }}>
                        <input type="number" min="0" step="0.01" placeholder={t('school:events.tickets')} value={eventForm.ticketCost} onChange={e => setEventForm({ ...eventForm, ticketCost: e.target.value })} style={inputStyle} />
                        <input type="number" min="0" step="0.01" placeholder={t('school:events.transfer')} value={eventForm.transferCost} onChange={e => setEventForm({ ...eventForm, transferCost: e.target.value })} style={inputStyle} />
                        <input type="number" min="0" step="0.01" placeholder={t('school:events.hotel')} value={eventForm.hotelCost} onChange={e => setEventForm({ ...eventForm, hotelCost: e.target.value })} style={inputStyle} />
                        <input type="number" min="0" step="0.01" placeholder={t('school:events.other')} value={eventForm.otherCost} onChange={e => setEventForm({ ...eventForm, otherCost: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '3fr auto', gap: '10px' }}>
                        <input placeholder={t('school:events.transferDetails')} value={eventForm.transferDetails} onChange={e => setEventForm({ ...eventForm, transferDetails: e.target.value })} style={inputStyle} />
                        <button type="submit" disabled={eventSaving} style={{
                            background: eventSaving ? '#94a3b8' : '#1A1A1A', color: 'white', border: 'none',
                            borderRadius: '10px', padding: '0 18px', cursor: eventSaving ? 'not-allowed' : 'pointer', fontWeight: '700'
                        }}>
                            {eventSaving ? t('school:events.adding') : t('school:events.add')}
                        </button>
                    </div>
                </form>

                {events.length === 0 ? (
                    <p style={{ color: '#888', margin: 0, fontSize: '14px' }}>{t('school:eventsSection.empty')}</p>
                ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                        {events.slice(0, 5).map(ev => (
                            <div key={ev.id} style={{ border: '1px solid #eee', borderRadius: '10px', padding: '10px 12px' }}>
                                <div style={{ fontWeight: '700' }}>{ev.title}</div>
                                <div style={{ fontSize: '13px', color: '#888' }}>
                                    {formatDate(ev.startDate)}
                                    {ev.city ? ` · ${ev.city}` : ''}
                                    {ev.location ? ` · ${ev.location}` : ''}
                                </div>
                                <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>
                                    {t('school:eventsSection.expenses', { amount: (ev.ticketCost + ev.transferCost + ev.hotelCost + ev.otherCost).toLocaleString('ru-RU') })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            )}

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px'
            }}>
                <QuickLink href="/students" icon="👥" label={canOwnSchools ? t('school:quickLinks.studentsOwner') : t('school:quickLinks.studentsCoach')} />
                <QuickLink href="/attendance" icon="📅" label={t('school:quickLinks.attendance')} />
                {canOwnSchools && (
                    <>
                        <QuickLink href="/staff" icon="🧑‍💼" label={t('school:quickLinks.staff')} />
                        <QuickLink href="/events" icon="🏁" label={t('school:quickLinks.eventsLink')} />
                        <QuickLink href="/subscriptions" icon="🎫" label={t('school:quickLinks.subscriptions')} />
                    </>
                )}
            </div>
            </div>
        </OwnerPageLayout>
    );
}

// Вспомогательные компоненты
function StatCard({ icon, label, value, color }) {
    return (
        <div style={{
            background: 'white', borderRadius: '10px',
            padding: '20px', textAlign: 'center',
            borderTop: `4px solid ${color}`
        }}>
            <div style={{ fontSize: '32px' }}>{icon}</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '8px 0' }}>
                {value}
            </div>
            <div style={{ color: '#888', fontSize: '14px' }}>{label}</div>
        </div>
    );
}

function QuickLink({ href, icon, label }) {
    return (
        <a href={href} className="dashboard-menu-card" style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '16px', fontSize: '15px', textDecoration: 'none', color: 'inherit'
        }}>
            <span style={{ fontSize: '24px' }}>{icon}</span>
            <span>{label}</span>
        </a>
    );
}